import type {
  BalanceResponseBody,
  CreateTopupRequest,
  CreateTopupResponse,
  GetTopupStatusResponse,
  PricingResponse,
  RedeemGiftCardRequest,
  RedeemGiftCardResponse,
  TransactionsResponse,
  TopupQuoteResponse,
} from '@nadoutong/contracts';
import { platformApiRequest } from './client.ts';

/**
 * Typed wrappers around platform-api `/v1/billing/*`.
 *
 * Ledger sign convention (matches public-billing.ts contracts):
 *   - Debits (charges) are negative.
 *   - Credits (gift-card redemptions, topups) are positive.
 *
 * Topup endpoints (`POST /v1/billing/topup`, `GET /v1/billing/topup/:id`)
 * use contracts camelCase shapes. PR P17 consolidated what was previously
 * a drifted local snake_case copy.
 */

const BASE_PATH = '/v1/billing';

export async function getBalance(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<BalanceResponseBody> {
  return platformApiRequest<BalanceResponseBody>(`${BASE_PATH}/balance`, { signal }, fetchImpl);
}

export interface TransactionsQuery {
  readonly limit?: number;
  readonly offset?: number;
  readonly from?: string;
  readonly to?: string;
}

export async function getTransactions(
  fetchImpl: typeof fetch,
  query?: TransactionsQuery,
  signal?: AbortSignal,
): Promise<TransactionsResponse> {
  const params = new URLSearchParams();
  if (query?.limit !== undefined) {
    params.set('limit', String(Math.max(1, Math.min(100, Math.floor(query.limit)))));
  }
  if (query?.offset !== undefined) {
    params.set('offset', String(Math.max(0, Math.floor(query.offset))));
  }
  if (typeof query?.from === 'string' && query.from.length > 0) {
    params.set('from', query.from);
  }
  if (typeof query?.to === 'string' && query.to.length > 0) {
    params.set('to', query.to);
  }
  const qs = params.toString();
  const path = qs.length > 0 ? `${BASE_PATH}/transactions?${qs}` : `${BASE_PATH}/transactions`;
  return platformApiRequest<TransactionsResponse>(path, { signal }, fetchImpl);
}

export async function getPricing(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<PricingResponse> {
  return platformApiRequest<PricingResponse>(`${BASE_PATH}/pricing`, { signal }, fetchImpl);
}

export async function redeemGiftCard(
  body: RedeemGiftCardRequest,
  fetchImpl: typeof fetch,
): Promise<RedeemGiftCardResponse> {
  return platformApiRequest<RedeemGiftCardResponse>(
    `${BASE_PATH}/redeem`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function createTopup(
  body: CreateTopupRequest,
  fetchImpl: typeof fetch,
): Promise<CreateTopupResponse> {
  return platformApiRequest<CreateTopupResponse>(
    `${BASE_PATH}/topup`,
    { method: 'POST', body },
    fetchImpl,
  );
}

export async function getTopupQuote(
  amountCnyCents: number,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<TopupQuoteResponse> {
  const params = new URLSearchParams({ amountCnyCents: String(amountCnyCents) });
  return platformApiRequest<TopupQuoteResponse>(
    `${BASE_PATH}/topup/quote?${params.toString()}`,
    { signal },
    fetchImpl,
  );
}

export async function getTopupStatus(
  orderId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetTopupStatusResponse> {
  return platformApiRequest<GetTopupStatusResponse>(
    `${BASE_PATH}/topup/${encodeURIComponent(orderId)}`,
    { signal },
    fetchImpl,
  );
}
